import React, { useEffect, useRef, useState } from 'react';
import { isWithinInterval, addDays, isValid } from 'date-fns';
import AvatarGroup from '@atlaskit/avatar-group';

import { useFlagsService, type FlagId } from '@atlassian/jira-flags';
import { JiraCommonFlag as Flag } from '@atlassian/jira-flags/src/common/ui/components/common-flag-v2/jira-flag';
import type { FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useTraits } from '@atlassian/jira-traits/src/TraitsStore.tsx';
import { getTraitByName } from '@atlassian/jira-traits/src/utils.tsx';
import { checkIfUserHasPermissionToAccessConfluence } from '../services/permission-check/index.tsx';
import { useFlagDismissed } from '../services/use-flag-dismissed/index.tsx';
import { useGetRecommendedUsers } from '../services/user-recommendation/index.tsx';
import messages from './messages.tsx';

// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const CONFLUENCE_URL = `https://${window.location.hostname}/wiki/`;

const END_RANGE = 31;
const START_RANGE = 7;

const isActive7to30DaysAgo = (accountCreationDate: string) => {
	const date = new Date(accountCreationDate);
	if (!isValid(date)) {
		return false;
	}
	const today = new Date();

	return isWithinInterval(today, {
		start: addDays(date, START_RANGE),
		end: addDays(date, END_RANGE),
	});
};

export const NudgeBundledUsersXflow = () => {
	const { formatMessage } = useIntl();
	const flagId = useRef<FlagId>();
	const { showFlag, dismissFlag } = useFlagsService();
	const accountId = useAccountId();
	const cloudId = useCloudId();
	const [traits, { fetchTraits }] = useTraits();
	const [permitted, setPermitted] = useState<boolean>(false);

	const [state, actions] = useFlagDismissed();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isReminderNudge = actions.canReShowFlag();

	useEffect(() => {
		fetchTraits(['SITE_USER'], {
			cloudId,
			accountId,
		});
	}, [accountId, cloudId, fetchTraits]);

	const jiraFirstActive = getTraitByName(traits, 'jira_first_active')?.value;
	const passesAgeCondition =
		typeof jiraFirstActive === 'string' ? isActive7to30DaysAgo(jiraFirstActive) : false;
	const confluenceFirstActiveDate = getTraitByName(traits, 'confluence_first_active')?.value;

	useEffect(() => {
		const fetchData = async () => {
			const confluenceAccess = await checkIfUserHasPermissionToAccessConfluence(cloudId);
			setPermitted(confluenceAccess.permitted);
		};
		if (passesAgeCondition && !confluenceFirstActiveDate) {
			fetchData();
		}
	}, [cloudId, confluenceFirstActiveDate, passesAgeCondition]);

	const isEligibleUser =
		passesAgeCondition && // jira_first_active + 7 < today < jira_first_active + 31
		!confluenceFirstActiveDate && // should not have logged into confluence
		permitted; // should have confluence access

	const skipRecommendedUsersCall = !isEligibleUser || (state.hasBeenDismissed && !isReminderNudge);

	const { loading, recommendedUsers } = useGetRecommendedUsers(skipRecommendedUsersCall);

	useEffect(() => {
		passesAgeCondition &&
			!confluenceFirstActiveDate &&
			fireOperationalAnalytics(createAnalyticsEvent({}), 'traits loaded', {
				jiraFirstActive,
				confluenceFirstActiveDate,
				permitted,
			});
	}, [
		confluenceFirstActiveDate,
		createAnalyticsEvent,
		isEligibleUser,
		jiraFirstActive,
		passesAgeCondition,
		permitted,
	]);

	useEffect(() => {
		if (
			isEligibleUser &&
			flagId.current == null &&
			!loading &&
			(!state.hasBeenDismissed || isReminderNudge) // should not have dismissed the flag (control lacks this condition)
		) {
			flagId.current = showFlag({
				render: (props: FlagCustomRenderProps) => (
					<Flag
						// @ts-expect-error Type error after Flag props have been fixed globally, to be fixed by owning team
						id={flagId}
						{...(recommendedUsers?.length
							? { icon: <AvatarGroup appearance="stack" data={recommendedUsers} maxCount={2} /> }
							: null)}
						messageId="nudge-bundled-users-to-join-connie.ui.flag.nudge-flag"
						messageType="engagement"
						testId="nudge-bundled-users-to-join-connie.ui.nudge-flag"
						title={formatMessage(messages.title)}
						description={formatMessage(messages.description)}
						flagType="info"
						isAutoDismiss={false}
						actions={[
							{
								content: formatMessage(messages.joinButton),
								onClick: () => {
									fireUIAnalytics(
										createAnalyticsEvent({}),
										'link clicked',
										'nudgeBundledUserFlagAction',
										{ isReminderNudge },
									);
									flagId.current && dismissFlag(flagId.current);
									actions.dismiss();

									// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
									window.open(CONFLUENCE_URL, '_blank');
								},
							},
						]}
						onDismissed={() => {
							if (flagId.current) {
								fireUIAnalytics(
									createAnalyticsEvent({}),
									'flag dismissed',
									'nudgeBundledUserFlagAction',
									{ isReminderNudge },
								);
								dismissFlag(flagId.current);
								actions.dismiss();
							}
						}}
						{...props}
					/>
				),
			});
			fireUIAnalytics(createAnalyticsEvent({}), 'flag viewed', 'nudgeBundledUserFlag', {
				isReminderNudge,
			});
		}
	}, [
		createAnalyticsEvent,
		dismissFlag,
		formatMessage,
		showFlag,
		isReminderNudge,
		actions,
		isEligibleUser,
		recommendedUsers,
		loading,
		state.hasBeenDismissed,
	]);
	return null;
};
